import clsx from 'clsx';
import { type ChangeEvent, type DragEvent, type ReactNode, useRef, useState } from 'react';
import {
  BiCamera,
  BiCheck,
  BiCloudUpload,
  BiFile,
  BiLockAlt,
  BiPencil,
  BiPlus,
  BiTrash,
  BiUpload,
} from 'react-icons/bi';
import { FaRegStar, FaStar } from 'react-icons/fa';

import { ButtonOutlined, ButtonOutlinedSmall } from '../Button';
import LoaderSpinner from '../LoaderSpinner/LoaderSpinner';

import s from './ProfileDoc.module.scss';

/**
 * The pieces a profile document is drawn from: a labelled value, a section
 * heading with its quiet Edit action, chip rows, repeatable entries, file rows
 * and the upload drop zone.
 *
 * Shared because every record that gets a page of its own is the same document
 * over different facts — a crew CV, a service provider, a marina — and they have
 * to be the same document, not several that look similar.
 */

/** Label above a value, the pairing every read-only block uses. */
export const Field = ({
  label,
  value,
  icon,
}: {
  label: string;
  value?: null | string;
  /** A glyph before the pair, for a short block of facts such as a side panel's. */
  icon?: ReactNode;
}) => (
  <div className={clsx(s.field, icon && s.fieldWithIcon)}>
    {icon && (
      <span className={s.fieldIcon} aria-hidden="true">
        {icon}
      </span>
    )}
    <span className={s.fieldLabel}>{label}</span>
    <span className={s.fieldValue}>{value || '—'}</span>
  </div>
);

/** A card's heading. `onEdit` is absent on a record that is not yours to
    change, and on sections whose editing lives in their own rows. */
export const SectionHeader = ({ title, onEdit }: { title: string; onEdit?: () => void }) => (
  <div className={s.sectionHeader}>
    <h2 className="headline-small">{title}</h2>
    {onEdit && (
      <button type="button" className={s.quietAction} onClick={onEdit}>
        <BiPencil aria-hidden="true" />
        Edit
      </button>
    )}
  </div>
);

/** Chip row — certifications, skills, cruising grounds and service categories
    all read the same way. */
export const ChipGroup = ({ label, items }: { label: string; items: string[] }) => (
  <div className={s.chipGroup}>
    <span className={s.fieldLabel}>{label}</span>
    {items.length === 0 ? (
      <span className={s.fieldValue}>—</span>
    ) : (
      <ul className={s.chips}>
        {items.map((item) => (
          <li className={s.chip} key={item}>
            {item}
          </li>
        ))}
      </ul>
    )}
  </div>
);

/**
 * An attached document. `onOpen` makes the row itself a control — used for a
 * stored file, which can be previewed; a file only just picked has nothing to
 * preview yet, so it gets a plain row.
 *
 * `note` replaces the size with what was done with the file — "We filled in the
 * details below" after a document is read. `onReplace` adds "Use another file",
 * which opens the picker and hands the new file straight up.
 */
export const FileRow = ({
  name,
  size,
  onOpen,
  onRemove,
  note,
  onReplace,
  replaceAccept,
}: {
  name: string;
  /** Bytes, as both `File` and the API's `CrewFile` report it. */
  size: number;
  onOpen?: () => void;
  onRemove?: () => void;
  /** Shown under the name, with a tick, in place of the size. */
  note?: string;
  /** Offers "Use another file"; receives the newly picked file. */
  onReplace?: (file: File) => void;
  /** The replace picker's `accept`. Any file when omitted. */
  replaceAccept?: string;
}) => {
  const replaceInput = useRef<HTMLInputElement>(null);
  const meta = note ? (
    <span className={clsx(s.fileMeta, s.fileNote)}>
      <BiCheck aria-hidden="true" />
      {note}
    </span>
  ) : (
    <span className={s.fileMeta}>{(size / 1024).toFixed(0)}kB</span>
  );

  return (
    <div className={s.fileRow} role={note ? 'status' : undefined}>
      <span className={s.fileIcon} aria-hidden="true">
        <BiFile />
      </span>
      {onOpen ? (
        <button type="button" className={`${s.fileText} ${s.fileTextButton}`} onClick={onOpen}>
          <span className={s.fileName}>{name}</span>
          {meta}
        </button>
      ) : (
        <span className={s.fileText}>
          <span className={s.fileName}>{name}</span>
          {meta}
        </span>
      )}
      {onReplace && (
        <>
          <ButtonOutlinedSmall type="button" onClick={() => replaceInput.current?.click()}>
            Use Another File
          </ButtonOutlinedSmall>
          <input
            ref={replaceInput}
            type="file"
            accept={replaceAccept}
            className={s.fileInput}
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) onReplace(file);
            }}
          />
        </>
      )}
      {onRemove && (
        <button
          type="button"
          className={s.fileRemove}
          aria-label={`Remove ${name}`}
          onClick={onRemove}
        >
          <BiTrash />
        </button>
      )}
    </div>
  );
};

/**
 * The shortlist toggle, said in words: under a name there is room for them,
 * where the catalogue card only had room for the star.
 *
 * Shared by every record that can be shortlisted — crew, providers, marinas —
 * because shortlisting is one act across the app and reading as three would
 * make it look like three.
 */
export const ShortlistButton = ({
  favorite,
  disabled,
  onToggle,
  iconOnly = false,
}: {
  favorite: boolean;
  /** True while the write is out; the toggle is not pressed twice. */
  disabled?: boolean;
  onToggle: () => void;
  /** Just the star, as the catalogue card draws it — for an aside whose corner
      holds it, with the words kept as its accessible name and tooltip. */
  iconOnly?: boolean;
}) => {
  const label = favorite ? 'Shortlisted' : 'Add to Shortlist';

  return (
    <button
      type="button"
      className={clsx(s.shortlist, iconOnly && s.shortlistIcon, favorite && s.shortlisted)}
      disabled={disabled}
      aria-pressed={favorite}
      aria-label={iconOnly ? 'Shortlist' : undefined}
      title={iconOnly ? label : undefined}
      onClick={onToggle}
    >
      {favorite ? <FaStar /> : <FaRegStar />}
      {!iconOnly && label}
    </button>
  );
};

/** The "+ label" ghost row that ends a repeatable list. */
export const AddRow = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button type="button" className={s.addRow} onClick={onClick}>
    <span className={s.addRowIcon} aria-hidden="true">
      <BiPlus />
    </span>
    {label}
  </button>
);

/** Header of one repeatable entry: which one it is, and what you can do to it.
    Both actions are absent on a CV you cannot edit. */
export const EntryHeader = ({
  label,
  onEdit,
  onRemove,
}: {
  label: string;
  onEdit?: () => void;
  onRemove?: () => void;
}) => (
  <div className={s.entryHeader}>
    <span className={s.entryLabel}>{label}</span>
    {(onEdit || onRemove) && (
      <div className={s.entryActions}>
        {onEdit && (
          <button type="button" className={s.quietAction} onClick={onEdit}>
            <BiPencil aria-hidden="true" />
            Edit
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            className={s.iconAction}
            aria-label={`Remove ${label}`}
            onClick={onRemove}
          >
            <BiTrash />
          </button>
        )}
      </div>
    )}
  </div>
);

/**
 * The document drop zone. A file can be dropped on it or picked.
 *
 * Without `title` it is the compact zone a profile document uses: a label around
 * a real file input rather than a styled div, so the whole zone is the control
 * that opens the picker and is reachable by keyboard, and the input stays in the
 * layout (hidden, not `display: none`) so it can still take focus.
 *
 * With `title` it is the zone a flow leads with — icon, title, hint, "Choose a
 * file", "Take a photo" when `camera` is set (pass `canScanDocuments()`: the
 * `capture` input only opens a camera on a phone or in the native app), and a
 * line saying what is read from the file. `reading` swaps it for a spinner while
 * the file is being read.
 *
 * @alias document upload, drop a file, drag and drop, upload area, scan a document
 */
export const DocumentDropzone = ({
  hint = 'PDF, max 1 file.',
  onPick,
  accept = 'application/pdf',
  title,
  privacy,
  camera = false,
  reading,
}: {
  hint?: string;
  onPick: (file: File) => void;
  /** As the input's own `accept`; a dropped file is held to it too. */
  accept?: string;
  /** Turns on the full zone with buttons. */
  title?: string;
  /** What is read from the file, said before anyone uploads. */
  privacy?: string;
  /** Offers "Take a photo" through the device camera. */
  camera?: boolean;
  /** The file being read — the zone shows a spinner instead. */
  reading?: string;
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const take = (file?: File | null) => {
    if (file && acceptsFile(file, accept)) onPick(file);
  };

  const pickFrom = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared so picking the same file twice in a row still fires a change.
    e.target.value = '';
    take(file);
  };

  const dropHandlers = {
    onDragOver: (e: DragEvent) => {
      e.preventDefault();
      setOver(true);
    },
    onDragLeave: () => setOver(false),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setOver(false);
      take(e.dataTransfer.files?.[0]);
    },
  };

  if (reading) {
    return (
      <div className={clsx(s.dropzone, s.dropzoneBusy)} role="status" aria-live="polite">
        <LoaderSpinner size={28} />
        <span className={s.dropzoneText}>Reading {reading}…</span>
      </div>
    );
  }

  if (!title) {
    return (
      <label className={clsx(s.dropzone, over && s.dropzoneOver)} {...dropHandlers}>
        <span className={s.dropzoneIcon} aria-hidden="true">
          <BiUpload />
        </span>
        <span className={s.dropzoneText}>
          Drag and drop files, or <span className={s.dropzoneLink}>choose files</span>.
        </span>
        <span className={s.fileMeta}>{hint}</span>
        <input type="file" accept={accept} className={s.fileInput} onChange={pickFrom} />
      </label>
    );
  }

  return (
    <div className={clsx(s.dropzone, s.dropzoneFull, over && s.dropzoneOver)} {...dropHandlers}>
      <span className={clsx(s.dropzoneIcon, s.dropzoneIconLarge)} aria-hidden="true">
        <BiCloudUpload />
      </span>
      <span className={s.dropzoneTitle}>{title}</span>
      <span className={s.dropzoneText}>{hint}</span>
      <div className={s.dropzoneActions}>
        <ButtonOutlined type="button" onClick={() => fileInput.current?.click()}>
          <BiFile aria-hidden="true" /> Choose a file
        </ButtonOutlined>
        {camera && (
          <ButtonOutlined type="button" onClick={() => cameraInput.current?.click()}>
            <BiCamera aria-hidden="true" /> Take a photo
          </ButtonOutlined>
        )}
      </div>
      {privacy && (
        <span className={s.dropzonePrivacy}>
          <BiLockAlt aria-hidden="true" />
          {privacy}
        </span>
      )}
      <input
        ref={fileInput}
        type="file"
        accept={accept}
        className={s.fileInput}
        tabIndex={-1}
        aria-hidden="true"
        onChange={pickFrom}
      />
      {camera && (
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          className={s.fileInput}
          tabIndex={-1}
          aria-hidden="true"
          onChange={pickFrom}
        />
      )}
    </div>
  );
};

/** The same zone under its first name, kept for the profile document. */
export const PdfDropzone = DocumentDropzone;

const acceptsFile = (file: File, accept: string) => {
  const parts = accept
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
  if (parts.length === 0) return true;
  return parts.some((part) => {
    if (part.startsWith('.')) return file.name.toLowerCase().endsWith(part);
    if (part.endsWith('/*')) return file.type.startsWith(part.slice(0, -1));
    return file.type === part;
  });
};
